import { useEffect, useState } from "react";
import { MoveHorizontal } from "lucide-react";
import axios from "axios";
import { API } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

const PHOTOS = {
  pileTires:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_clutter-free-8/artifacts/ih8xdq9p_IMG_0442.jpeg",
  pileWide:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_clutter-free-8/artifacts/q4mye1q1_IMG_0444.jpeg",
  trailerLoaded:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_clutter-free-8/artifacts/1hc1qbhu_IMG_0446.jpeg",
  trailerEmpty:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_clutter-free-8/artifacts/h86jpo6q_IMG_0450.jpeg",
  yardClear:
    "https://customer-assets-eiarnc6j.emergentagent.net/job_clutter-free-8/artifacts/4gpig9n2_IMG_0452.jpeg",
};

const FALLBACK_PAIRS = [
  { label: "Property Cleanout", before: PHOTOS.pileTires, after: PHOTOS.yardClear },
  { label: "Full Truckload Haul", before: PHOTOS.trailerLoaded, after: PHOTOS.trailerEmpty },
];

export const BeforeAfterSlider = ({ before, after, label }) => {
  const [pos, setPos] = useState(50);
  const testId = `gallery-slider-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div data-testid={testId} className="group relative aspect-[4/3] overflow-hidden border border-seam bg-coal">
      <img src={after} alt={`${label} — after`} loading="lazy" className="absolute inset-0 h-full w-full object-cover" />
      <img
        src={before}
        alt={`${label} — before`}
        loading="lazy"
        className="absolute inset-0 h-full w-full object-cover grayscale"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
      />
      <span className="absolute left-4 top-4 bg-ink/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-white">
        Before
      </span>
      <span className="absolute right-4 top-4 bg-acid px-3 py-1 text-[10px] font-bold uppercase tracking-[0.25em] text-black">
        After
      </span>
      <div className="pointer-events-none absolute inset-y-0" style={{ left: `${pos}%` }}>
        <div className="absolute inset-y-0 -ml-px w-0.5 bg-acid" />
        <div className="absolute top-1/2 flex h-10 w-10 -translate-x-1/2 -translate-y-1/2 items-center justify-center border border-acid bg-ink">
          <MoveHorizontal className="h-4 w-4 text-acid" />
        </div>
      </div>
      <input
        type="range"
        min={0}
        max={100}
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label={`Drag to compare before and after for ${label}`}
        className="absolute inset-0 h-full w-full cursor-ew-resize opacity-0"
      />
      <p className="pointer-events-none absolute bottom-4 left-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-white [text-shadow:0_1px_8px_rgba(0,0,0,0.8)]">
        {label}
      </p>
    </div>
  );
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveUrl = (u) => (u && u.startsWith("http") ? u : `${BACKEND_URL}${u}`);

export const Gallery = () => {
  const [pairs, setPairs] = useState(FALLBACK_PAIRS);

  useEffect(() => {
    axios
      .get(`${API}/gallery`)
      .then(({ data }) => {
        if (Array.isArray(data) && data.length) {
          setPairs(
            data.map((p) => ({
              label: p.label,
              before: resolveUrl(p.before_url),
              after: resolveUrl(p.after_url),
            }))
          );
        }
      })
      .catch(() => {});
  }, []);

  return (
  <section id="gallery" data-testid="gallery-section" className="border-y border-seam bg-coal">
    <div className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-32">
      <Reveal>
        <p className="mb-4 text-xs font-bold uppercase tracking-[0.35em] text-acid">Before & after</p>
        <h2 className="mb-6 font-display text-4xl font-black italic uppercase leading-none tracking-tighter sm:text-5xl lg:text-[5vw]">
          Drag the line. <span className="text-acid">See the leap.</span>
        </h2>
        <p className="mb-16 max-w-xl text-base leading-relaxed text-zinc-400">
          Every job ends the same way: an empty, swept-clean space and a customer who can’t
          believe it was ever that full.
        </p>
      </Reveal>

      <div className="grid gap-6 md:grid-cols-3 md:gap-8">
        {pairs.map((p, i) => (
          <Reveal key={`${p.label}-${i}`} delay={i * 0.08}>
            <BeforeAfterSlider {...p} />
          </Reveal>
        ))}
        <Reveal delay={0.16}>
          <div data-testid="gallery-crew-card" className="group relative aspect-[4/3] overflow-hidden border border-seam">
            <img
              src={PHOTOS.pileWide}
              alt="A Hanover-area property piled with tires and debris before cleanout"
              loading="lazy"
              className="h-full w-full object-cover grayscale transition-all duration-700 group-hover:scale-105 group-hover:grayscale-0"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
            <p className="absolute bottom-4 left-4 font-display text-xl font-extrabold italic uppercase tracking-tight text-white">
              Every job starts somewhere
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
  );
};
